import React, { useState, useEffect } from 'react';
import { FabrikUser } from '../../../types';
import { format } from 'date-fns';
import { downloadCSV } from '../../../utils/csvExport';

interface ManageUsersTableProps {
  users: FabrikUser[];
  allUsers: FabrikUser[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  onViewDetails: (userId: string) => void;
}

type SortableField = 
  | 'name' 
  | 'email' 
  | 'membership_tier'
  | 'total_checkins_count'
  | 'gathering_checkins_count'
  | 'last_gathering_checkin'
  | 'last_space_checkin'
  | 'hosted_gatherings_count'
  | 'last_hosted_gathering'
  | 'notes_count'
  | 'created_at';

type SortDirection = 'asc' | 'desc';

const ManageUsersTable: React.FC<ManageUsersTableProps> = ({
  users,
  allUsers,
  isLoading,
  error,
  refresh,
  onViewDetails,
}) => {
  const [filterText, setFilterText] = useState<string>('');
  const [filteredUsers, setFilteredUsers] = useState<FabrikUser[]>(users);
  const [sortField, setSortField] = useState<SortableField>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [userFilter, setUserFilter] = useState<'all' | 'members' | 'caas'>('all');
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    // Always start with all users to ensure sorting works across all data
    let filtered = allUsers;

    // Apply user filter
    if (userFilter === 'members') {
      filtered = filtered.filter(user => user.usage_data?.membership_tier);
    } else if (userFilter === 'caas') {
      filtered = filtered.filter(user => user.caas_data?.is_caas_user);
    }

    // Apply text search filter (search across all users when filtering)
    if (filterText.trim()) {
      const lowercaseFilter = filterText.toLowerCase();
      filtered = filtered.filter(user => {
        // Build combined fields for robust matching (like UsersTable)
        const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim().toLowerCase();
        const reverseFullName = `${user.last_name || ''} ${user.first_name || ''}`.trim().toLowerCase();
        const displayName = (user.display_name || '').toLowerCase();
        const email = (user.email || '').toLowerCase();
        const userId = (user.id || '').toLowerCase();
        
        // Aggregate into a single haystack for substring search
        const haystack = [fullName, reverseFullName, displayName, email, userId]
          .filter(Boolean)
          .join(' ');
        
        return haystack.includes(lowercaseFilter);
      });
    }
    
    setFilteredUsers(filtered);
  }, [filterText, userFilter, allUsers]);

  // Helper function to get user's display name
  const getUserName = (user: FabrikUser): string => {
    if (user.first_name || user.last_name) {
      return `${user.first_name || ''} ${user.last_name || ''}`.trim();
    }
    return user.display_name || 'N/A';
  };

  // Helper function to format date with time
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Never';
    try {
      return format(new Date(dateString), 'MMM d, yyyy h:mm a');
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Helper function to format date only (no time)
  const formatDateOnly = (dateString: string | null | undefined): string => {
    if (!dateString) return '—';
    try {
      return format(new Date(dateString), 'MMM d, yyyy');
    } catch (error) {
      return 'Invalid date';
    }
  };

  // Helper function to get membership status
  const getMembershipStatus = (user: FabrikUser): string => {
    return user.usage_data?.membership_tier || 'No membership';
  };

  // Handle column header click for sorting (matching gatherings table pattern)
  const handleSort = (field: SortableField) => {
    if (field === sortField) {
      // Toggle direction if clicking the same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new sort field and default to ascending
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sort indicator for column headers (matching gatherings table pattern)
  const getSortIndicator = (field: SortableField) => {
    if (field !== sortField) return null;
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  // Sort filtered users
  const sortedUsers = React.useMemo(() => {
    return [...filteredUsers].sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortField) {
        case 'name':
          aValue = getUserName(a).toLowerCase();
          bValue = getUserName(b).toLowerCase();
          break;
        case 'email':
          aValue = a.email.toLowerCase();
          bValue = b.email.toLowerCase();
          break;
        case 'membership_tier':
          aValue = getMembershipStatus(a).toLowerCase();
          bValue = getMembershipStatus(b).toLowerCase();
          break;
        case 'total_checkins_count':
          aValue = a.usage_data?.total_checkins_count || 0;
          bValue = b.usage_data?.total_checkins_count || 0;
          break;
        case 'gathering_checkins_count':
          aValue = a.usage_data?.gathering_checkins_count || 0;
          bValue = b.usage_data?.gathering_checkins_count || 0;
          break;
        case 'last_gathering_checkin':
          aValue = a.usage_data?.last_gathering_checkin ? new Date(a.usage_data.last_gathering_checkin).getTime() : 0;
          bValue = b.usage_data?.last_gathering_checkin ? new Date(b.usage_data.last_gathering_checkin).getTime() : 0;
          break;
        case 'last_space_checkin':
          aValue = a.usage_data?.last_space_checkin ? new Date(a.usage_data.last_space_checkin).getTime() : 0;
          bValue = b.usage_data?.last_space_checkin ? new Date(b.usage_data.last_space_checkin).getTime() : 0;
          break;
        case 'hosted_gatherings_count':
          aValue = a.usage_data?.hosted_gatherings_count || 0;
          bValue = b.usage_data?.hosted_gatherings_count || 0;
          break;
        case 'last_hosted_gathering':
          aValue = a.usage_data?.last_hosted_gathering ? new Date(a.usage_data.last_hosted_gathering).getTime() : 0;
          bValue = b.usage_data?.last_hosted_gathering ? new Date(b.usage_data.last_hosted_gathering).getTime() : 0;
          break;
        case 'notes_count':
          aValue = a.notes_count || 0;
          bValue = b.notes_count || 0;
          break;
        case 'created_at':
          aValue = a.created_at ? new Date(a.created_at).getTime() : 0;
          bValue = b.created_at ? new Date(b.created_at).getTime() : 0;
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredUsers, sortField, sortDirection]);

  // Export users to CSV
  const handleExportCSV = () => {
    setIsExporting(true);
    
    try {
      // Transform sorted users to CSV format
      const csvData = sortedUsers.map(user => {
        const baseData: Record<string, string> = {
          'Name': getUserName(user),
          'Email': user.email,
          'Member': getMembershipStatus(user),
          'Join Date': formatDateOnly(user.created_at),
          'CaaS': user.caas_data?.is_caas_user ? 'Yes' : 'No',
          'CaaS Team': user.caas_data?.teams?.[0]?.team_name || 'N/A',
          'CaaS Client': user.caas_data?.teams?.[0]?.client_name || 'N/A',
        };
        
        // Add Comms column for CaaS filter
        if (userFilter === 'caas') {
          baseData['Comms'] = user.opted_in_comms === true ? 'Yes' : user.opted_in_comms === false ? 'No' : 'N/A';
        }
        
        const result: Record<string, string | number> = {
          ...baseData,
          'Notes': user.notes_count ?? 'N/A',
        };
        
        // Only include space check-in columns for non-CaaS filters
        if (userFilter !== 'caas') {
          result['In Space'] = user.usage_data?.total_checkins_count ?? 'N/A';
        }
        
        result['Gatherings Attended'] = user.usage_data?.gathering_checkins_count ?? 'N/A';
        
        // Only include hosting columns for non-CaaS filters
        if (userFilter !== 'caas') {
          result['Gatherings Hosted'] = user.usage_data?.hosted_gatherings_count ?? 'N/A';
        }
        
        if (userFilter !== 'caas') {
          result['Last Visit'] = formatDate(user.usage_data?.last_space_checkin);
        }
        
        result['Last Gathering'] = formatDate(user.usage_data?.last_gathering_checkin);
        
        if (userFilter !== 'caas') {
          result['Last Hosted'] = formatDate(user.usage_data?.last_hosted_gathering);
        }
        
        return result;
      });
      
      // Generate filename
      const timestamp = new Date().toISOString().split('T')[0];
      const filterLabel = userFilter === 'members' ? 'members' : userFilter === 'caas' ? 'caas' : 'all-users';
      const filename = `users-${filterLabel}-${timestamp}.csv`;
      
      // Download CSV
      downloadCSV(csvData, filename);
    } catch (err) {
      console.error('Failed to export users:', err);
      alert('Failed to export users to CSV');
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading && users.length === 0) {
    return <div className="table-loading">Loading users...</div>;
  }

  if (error && users.length === 0) {
    return (
      <div className="table-error">
        <p>{error}</p>
        <button onClick={refresh}>Retry</button>
      </div>
    );
  }

  return (
    <div className="table-view">
      {isLoading && users.length > 0 && (
        <div className="background-loading-indicator">
          <div className="spinner-small"></div>
          <span>Loading users...</span>
        </div>
      )}
      
      {error && users.length > 0 && (
        <div className="background-error-indicator">
          <p>{error}</p>
          <button onClick={refresh} className="small-button">Retry</button>
        </div>
      )}

      {/* Filters and Search */}
      <div className="users-filter-section">
        <div className="users-filter-row">
          <div className="users-search-box">
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="users-filter-dropdown">
            <select
              id="user-filter"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value as 'all' | 'members' | 'caas')}
              className="filter-select"
            >
              <option value="all">All Users</option>
              <option value="members">Members Only</option>
              <option value="caas">CaaS Only</option>
            </select>
          </div>
          <button 
            className="clear-filters-button"
            onClick={() => {
              setFilterText('');
              setUserFilter('all');
            }}
            disabled={!filterText && userFilter === 'all'}
            title="Clear all filters"
          >
            Clear
          </button>
          
          <button
            className="export-csv-button"
            onClick={handleExportCSV}
            disabled={isExporting || sortedUsers.length === 0}
            title={`Export ${sortedUsers.length} users to CSV`}
          >
            {isExporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
        {(filterText || userFilter !== 'all') && sortedUsers.length < allUsers.length && (
          <div className="filter-results-count">
            Showing {sortedUsers.length} of {allUsers.length} users
          </div>
        )}
      </div>
      
      {sortedUsers.length === 0 ? (
        <div className="table-empty">
          No users found matching your filters.
        </div>
      ) : (
        <table className="users-table">
          <thead>
            <tr>
              <th onClick={() => handleSort('name')} className="sortable-header" title="User's full name">
                Name{getSortIndicator('name')}
              </th>
              <th onClick={() => handleSort('email')} className="sortable-header" title="User's email address">
                Email{getSortIndicator('email')}
              </th>
              <th onClick={() => handleSort('membership_tier')} className="sortable-header" title="Membership status">
                Member{getSortIndicator('membership_tier')}
              </th>
              <th onClick={() => handleSort('created_at')} className="sortable-header" title="Date user joined Fabrik">
                Join Date{getSortIndicator('created_at')}
              </th>
              <th title="CaaS user status">
                CaaS
              </th>
              <th title="CaaS team and client">
                CaaS Team/Client
              </th>
              {userFilter === 'caas' && (
                <th title="Opted in to communications">
                  Comms
                </th>
              )}
              <th onClick={() => handleSort('notes_count')} className="sortable-header" title="Number of admin notes">
                Notes{getSortIndicator('notes_count')}
              </th>
              {userFilter !== 'caas' && (
                <th onClick={() => handleSort('total_checkins_count')} className="sortable-header" title="Total space check-ins">
                  In Space{getSortIndicator('total_checkins_count')}
                </th>
              )}
              <th onClick={() => handleSort('gathering_checkins_count')} className="sortable-header" title="Total gathering check-ins">
                Gatherings Attended{getSortIndicator('gathering_checkins_count')}
              </th>
              {userFilter !== 'caas' && (
                <th onClick={() => handleSort('hosted_gatherings_count')} className="sortable-header" title="Total gatherings hosted">
                  Gatherings Hosted{getSortIndicator('hosted_gatherings_count')}
                </th>
              )}
              {userFilter !== 'caas' && (
                <th onClick={() => handleSort('last_space_checkin')} className="sortable-header" title="Last space check-in date">
                  Last Visit{getSortIndicator('last_space_checkin')}
                </th>
              )}
              <th onClick={() => handleSort('last_gathering_checkin')} className="sortable-header" title="Last gathering check-in date">
                Last Gathering{getSortIndicator('last_gathering_checkin')}
              </th>
              {userFilter !== 'caas' && (
                <th onClick={() => handleSort('last_hosted_gathering')} className="sortable-header" title="Last gathering hosted date">
                  Last Hosted{getSortIndicator('last_hosted_gathering')}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {sortedUsers.map((user) => (
            <tr 
              key={user.id} 
              onClick={() => onViewDetails(user.id)}
              className="table-row"
            >
              <td className="user-name-cell">
                <div className="user-info">
                  {user.image_url && (
                    <div className="user-avatar">
                      <img src={user.image_url} alt={getUserName(user)} />
                    </div>
                  )}
                  <div className="user-details">
                    <div className="user-name">{getUserName(user)}</div>
                  </div>
                </div>
              </td>
              <td>{user.email}</td>
              <td>
                {user.usage_data?.membership_tier ? (
                  <span 
                    className="status-badge membership-yes membership-with-info" 
                    data-tooltip={`Member: ${user.usage_data.membership_tier}`}
                  >
                    Yes <span className="info-indicator">ⓘ</span>
                  </span>
                ) : (
                  <span className="status-badge membership-no">No</span>
                )}
              </td>
              <td className="checkin-date">{formatDateOnly(user.created_at)}</td>
              <td>
                {user.caas_data?.is_caas_user ? (
                  <span className="status-badge membership-yes">Yes</span>
                ) : (
                  <span className="status-badge membership-no">No</span>
                )}
              </td>
              <td className="caas-team-cell">
                {user.caas_data?.teams && user.caas_data.teams.length > 0 ? (
                  <div className="caas-team-info-table">
                    {user.caas_data.teams.map((team, index) => (
                      <div key={team.team_id} className="caas-team-stack">
                        <div className="caas-client-name-table">{team.client_name}</div>
                        <div className="caas-team-name-table">{team.team_name}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="caas-empty">—</span>
                )}
              </td>
              {userFilter === 'caas' && (
                <td className="comms-status-cell">
                  {user.opted_in_comms === true ? (
                    <span className="status-icon status-yes" title="Opted in to communications">✓</span>
                  ) : user.opted_in_comms === false ? (
                    <span className="status-icon status-no" title="Not opted in to communications">✗</span>
                  ) : (
                    <span className="status-icon status-empty" title="Unknown">—</span>
                  )}
                </td>
              )}
              <td className="checkin-count">{user.notes_count ?? 'N/A'}</td>
              {userFilter !== 'caas' && (
                <td className="checkin-count">{user.usage_data?.total_checkins_count ?? 'N/A'}</td>
              )}
              <td className="checkin-count">{user.usage_data?.gathering_checkins_count ?? 'N/A'}</td>
              {userFilter !== 'caas' && (
                <td className="checkin-count">{user.usage_data?.hosted_gatherings_count ?? 'N/A'}</td>
              )}
              {userFilter !== 'caas' && (
                <td className="checkin-date">{formatDate(user.usage_data?.last_space_checkin)}</td>
              )}
              <td className="checkin-date">{formatDate(user.usage_data?.last_gathering_checkin)}</td>
              {userFilter !== 'caas' && (
                <td className="checkin-date">{formatDate(user.usage_data?.last_hosted_gathering)}</td>
              )}
            </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default ManageUsersTable;
